import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Palette, RefreshCw, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fetchAPI = async (endpoint, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro na requisição');
  }
  return response.json();
};

const Form3DConfig = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('internal_colors');
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Formulário de adição
  const [newItem, setNewItem] = useState({ name: '', description: '' });

  // Buscar dados
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['form-3d-options'],
    queryFn: () => fetchAPI('/form-3d/options'),
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (endpoint) => fetchAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(newItem),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['form-3d-options']);
      setNewItem({ name: '', description: '' });
      toast.success('Item criado com sucesso!');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation para excluir
  const deleteMutation = useMutation({
    mutationFn: ({ endpoint, id }) => fetchAPI(`${endpoint}/${id}`, {
      method: 'DELETE',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['form-3d-options']);
      toast.success('Item removido com sucesso!');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const options = data?.data || {};
  const internalColors = options.internalColors || [];
  const externalColors = options.externalColors || [];
  const handleModels = options.handleModels || [];

  const handleCreate = () => {
    if (!newItem.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    let endpoint = '';
    switch (activeTab) {
      case 'internal_colors':
        endpoint = '/form-3d/colors/internal';
        break;
      case 'external_colors':
        endpoint = '/form-3d/colors/external';
        break;
      default:
        return;
    }

    createMutation.mutate(endpoint);
  };

  const handleDelete = (id) => {
    setConfirmDelete({ id });
  };

  const confirmDeleteAction = () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    let endpoint = '';
    switch (activeTab) {
      case 'internal_colors':
        endpoint = '/form-3d/colors/internal';
        break;
      case 'external_colors':
        endpoint = '/form-3d/colors/external';
        break;
      default:
        return;
    }
    deleteMutation.mutate({ endpoint, id });
    setConfirmDelete(null);
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'internal_colors':
        return internalColors;
      case 'external_colors':
        return externalColors;
      case 'handles':
        return handleModels;
      default:
        return [];
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-7 h-7" />
          Configuração de Acabamento 3D
        </h1>
        <p className="text-gray-600 mt-1">
          Gerencie as opções de cores para o formulário de acabamento 3D
        </p>
        <p className="text-sm text-gray-500 mt-1">
          <strong>Nota:</strong> Os puxadores são cadastrados no sistema de Estoque → Produtos, na categoria "Puxadores"
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('internal_colors')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'internal_colors'
              ? 'border-b-2 border-purple-500 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Palette className="w-4 h-4 inline mr-2" />
          Cores Internas
        </button>
        <button
          onClick={() => setActiveTab('external_colors')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'external_colors'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Palette className="w-4 h-4 inline mr-2" />
          Cores Externas
        </button>
      </div>

      {/* Aviso sobre puxadores */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <ExternalLink className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="font-medium text-amber-800">Puxadores</h3>
            <p className="text-sm text-amber-700">
              Os modelos de puxadores são cadastrados no sistema de <strong>Estoque</strong>.
              Acesse <Link to="/estoque" className="underline font-medium">Estoque → Produtos</Link> e cadastre os puxadores na categoria "Puxadores".
            </p>
          </div>
        </div>
      </div>

      {/* Formulário de adição (apenas para cores) */}
      {activeTab !== 'handles' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Adicionar Nova Cor</h2>
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Nome da cor"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
              Adicionar
            </button>
          </div>
        </div>
      )}

      {/* Lista de itens */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Nome
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-center">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                </td>
              </tr>
            ) : getCurrentItems().length === 0 ? (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-center text-gray-500">
                  Nenhum item cadastrado
                </td>
              </tr>
            ) : (
              getCurrentItems().map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-900">{item.name}</td>
                  <td className="px-6 py-4 text-right">
                    {activeTab !== 'handles' && (
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Confirmar exclusão"
        message="Tem certeza que deseja remover este item?"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Form3DConfig;
